import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { fetchStrongsDefinition, fetchWebsterDefinition, type StrongsDefinition } from '@api/index';
import {
  CloseIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ContractIcon,
  ExpandIcon,
  LoadingSpinner,
} from '@components/icons';
import { DefinitionText } from './definition-text';
import { DefinitionTabs, type DefinitionTab } from './definition-tabs';
import { WebsterEntry } from './webster-entry';

interface HistoryEntry {
  numbers: string;
  englishWord?: string;
  websterDefinition?: string;
}

interface StrongsPanelProps {
  numbers: string;
  isVisible: boolean;
  onClose: () => void;
  englishWord?: string;
  websterDefinition?: string;
  /** Verse analysis panel is open — shift right so both drawers coexist */
  isSplit?: boolean;
}

/**
 * Strong's definition panel component - Liquid Glass drawer.
 */
export function StrongsPanel({ numbers, isVisible, onClose, englishWord, websterDefinition, isSplit }: StrongsPanelProps) {
  const [definitions, setDefinitions] = useState<(StrongsDefinition | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentNumbers, setCurrentNumbers] = useState(numbers);
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    isVisible ? 'entering' : 'exiting',
  );
  const [contentBounce, setContentBounce] = useState(false);
  const [fetchedWebster, setFetchedWebster] = useState<string | null>(null);
  const [isWebsterMissing, setIsWebsterMissing] = useState(false);
  const [currentEnglishWord, setCurrentEnglishWord] = useState(englishWord);
  const [currentWebsterDef, setCurrentWebsterDef] = useState(websterDefinition);
  // Sticky across words while the panel is open — reading Webster's word by word
  const [activeTab, setActiveTab] = useState<DefinitionTab>('strongs');
  const [isZoomed, setIsZoomed] = useState(false);
  const prevNumbersRef = useRef(numbers);

  // Navigation history
  const historyRef = useRef<HistoryEntry[]>([{ numbers, englishWord, websterDefinition }]);
  const historyIndexRef = useRef(0);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const isNavigatingRef = useRef(false);

  // Fetch Webster's definition when englishWord is provided but no definition was pre-supplied
  useEffect(() => {
    setFetchedWebster(null);
    setIsWebsterMissing(false);
    if (!currentEnglishWord || currentWebsterDef) return;

    fetchWebsterDefinition(currentEnglishWord).then((def) => {
      if (def) setFetchedWebster(def);
      else setIsWebsterMissing(true);
    });
  }, [currentEnglishWord, currentWebsterDef, currentNumbers]);

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      // After animation completes, set to visible
      const timer = setTimeout(() => setAnimationState('visible'), 500);
      return () => clearTimeout(timer);
    } else {
      setIsZoomed(false);
      setAnimationState('exiting');
      // After exit animation, unmount
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  // Push to history when numbers prop changes externally
  useEffect(() => {
    if (!numbers) return;

    // Trigger bounce if panel is already visible and numbers changed
    if (shouldRender && prevNumbersRef.current !== numbers) {
      setContentBounce(true);
      setTimeout(() => setContentBounce(false), 300);
    }
    prevNumbersRef.current = numbers;

    // Push to history if this is a new external navigation (not from back/forward)
    if (!isNavigatingRef.current) {
      const history = historyRef.current;
      const idx = historyIndexRef.current;
      // Truncate any forward history and push new entry
      historyRef.current = [...history.slice(0, idx + 1), { numbers, englishWord, websterDefinition }];
      historyIndexRef.current = historyRef.current.length - 1;
      // But skip the duplicate push on initial mount (already seeded in useState)
      if (historyRef.current.length === 2 && historyRef.current[0].numbers === numbers) {
        historyRef.current = [historyRef.current[0]];
        historyIndexRef.current = 0;
      }
      setCanGoBack(historyIndexRef.current > 0);
      setCanGoForward(false);
    }
    isNavigatingRef.current = false;

    setCurrentNumbers(numbers);
    setCurrentEnglishWord(englishWord);
    setCurrentWebsterDef(websterDefinition);
    setLoading(true);

    const numberList = numbers.split(',').map((n) => n.trim());
    Promise.all(numberList.map((n) => fetchStrongsDefinition(n))).then((results) => {
      setDefinitions(results);
      setLoading(false);
    });
  }, [numbers, shouldRender, englishWord, websterDefinition]);

  // Push a new entry to history and load it
  const navigateToNumber = useCallback((entry: HistoryEntry) => {
    const history = historyRef.current;
    const idx = historyIndexRef.current;
    // Truncate forward history and push
    historyRef.current = [...history.slice(0, idx + 1), entry];
    historyIndexRef.current = historyRef.current.length - 1;
    setCanGoBack(true);
    setCanGoForward(false);

    setCurrentNumbers(entry.numbers);
    setCurrentEnglishWord(entry.englishWord);
    setCurrentWebsterDef(entry.websterDefinition);
    setFetchedWebster(null);
    setContentBounce(true);
    setTimeout(() => setContentBounce(false), 300);
    setLoading(true);
    const numberList = entry.numbers.split(',').map((n) => n.trim());
    Promise.all(numberList.map((n) => fetchStrongsDefinition(n))).then((results) => {
      setDefinitions(results);
      setLoading(false);
    });
  }, []);

  // Handle clicking a link in the definition to show related strongs
  const handleStrongsLinkClick = useCallback((strongsNumber: string) => {
    navigateToNumber({ numbers: strongsNumber });
  }, [navigateToNumber]);

  // Load a history entry by index (for back/forward)
  const loadHistoryEntry = useCallback((index: number) => {
    const entry = historyRef.current[index];
    if (!entry) return;
    historyIndexRef.current = index;
    setCanGoBack(index > 0);
    setCanGoForward(index < historyRef.current.length - 1);

    setCurrentNumbers(entry.numbers);
    setCurrentEnglishWord(entry.englishWord);
    setCurrentWebsterDef(entry.websterDefinition);
    setFetchedWebster(null);
    setContentBounce(true);
    setTimeout(() => setContentBounce(false), 300);
    setLoading(true);
    const numberList = entry.numbers.split(',').map((n) => n.trim());
    Promise.all(numberList.map((n) => fetchStrongsDefinition(n))).then((results) => {
      setDefinitions(results);
      setLoading(false);
    });
  }, []);

  const handleGoBack = useCallback(() => {
    if (historyIndexRef.current > 0) {
      loadHistoryEntry(historyIndexRef.current - 1);
    }
  }, [loadHistoryEntry]);

  const handleGoForward = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      loadHistoryEntry(historyIndexRef.current + 1);
    }
  }, [loadHistoryEntry]);

  // Escape steps out of zoom back to the drawer (the drawer itself stays open)
  useEffect(() => {
    if (!isZoomed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isZoomed]);

  if (!shouldRender) return null;

  // Get the first definition for header display
  const firstDef = definitions[0];
  // Webster's needs an English word — cross-ref hops (G1169 from a link) have none
  const hasWebsterTab = !!currentEnglishWord;
  const isWebsterShown = hasWebsterTab && activeTab === 'webster';
  const headerBtnClass =
    '-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200';

  const panel = (
    <div
      className={`strongs-panel ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''} ${contentBounce ? 'content-bounce' : ''} ${isSplit && !isZoomed ? 'split-right' : ''} ${isZoomed ? 'strongs-panel-zoomed' : ''}`}
    >
      {/* Fixed header section */}
      <div className="strongs-panel-header">
        <div className="mb-2 flex items-start justify-between">
          <h3 className="text-2xl font-semibold text-stone-800 dark:text-stone-200">
            Strong's: {currentNumbers}
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={handleGoBack}
              disabled={!canGoBack}
              className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 disabled:opacity-30 disabled:hover:bg-transparent dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200 dark:disabled:hover:bg-transparent"
              title="Back"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={handleGoForward}
              disabled={!canGoForward}
              className="-m-1 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 disabled:opacity-30 disabled:hover:bg-transparent dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200 dark:disabled:hover:bg-transparent"
              title="Forward"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setIsZoomed((z) => !z)}
              className={headerBtnClass}
              title={isZoomed ? 'Exit zoom' : 'Zoom'}
            >
              {isZoomed ? <ContractIcon className="h-5 w-5" /> : <ExpandIcon className="h-5 w-5" />}
            </button>
            <button onClick={onClose} className={headerBtnClass} title="Close">
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Native text and pronunciation - always visible */}
        {!loading && firstDef && (
          <>
            <div className="mb-1 text-4xl font-semibold text-stone-800 dark:text-stone-200">
              {firstDef.metadata.native}
            </div>
            <div className="text-xl text-stone-600 dark:text-stone-400">
              <span className="font-medium">Pronounced:</span>{' '}
              <span className="italic">{firstDef.metadata.pronunciation}</span>
            </div>
            {hasWebsterTab && <DefinitionTabs activeTab={activeTab} onChange={setActiveTab} />}
          </>
        )}

        {loading && (
          <div className="flex items-center gap-3 text-xl text-stone-500 dark:text-stone-400">
            <LoadingSpinner className="h-7 w-7" />
            <span>Loading definition...</span>
          </div>
        )}
      </div>

      {/* Scrollable definition content */}
      {!loading && (
        <div className="strongs-panel-content">
          {isWebsterShown ? (
            <WebsterEntry
              englishWord={currentEnglishWord!}
              definition={currentWebsterDef || fetchedWebster}
              isMissing={isWebsterMissing}
            />
          ) : (
            definitions.map((def, index) =>
              def ? (
                <div key={`${def.id}-${index}`} className="strongs-definition-text">
                  <div className="text-xl leading-relaxed text-stone-700 dark:text-stone-300">
                    <span className="font-medium text-stone-500 dark:text-stone-400">
                      {def.metadata.number}:
                    </span>{' '}
                    <DefinitionText
                      definition={def.metadata.definition}
                      language={def.metadata.language}
                      onLinkClick={handleStrongsLinkClick}
                    />
                  </div>
                </div>
              ) : (
                <div key={index} className="text-xl italic text-stone-500 dark:text-stone-400">
                  Definition not found
                </div>
              ),
            )
          )}
        </div>
      )}
    </div>
  );

  // Zoomed: a centered reading-width modal. Portaled to <body> so no
  // ancestor backdrop-filter can trap the fixed overlay.
  if (isZoomed) {
    return createPortal(
      <>
        <div className="strongs-zoom-backdrop" onClick={() => setIsZoomed(false)} />
        {panel}
      </>,
      document.body,
    );
  }
  return panel;
}
